import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { threeShapeTokenLoginTemplate } from 'apollo/templates/scanner-case';
import { threeShapeClinicLoginTemplate, threeShapeClinicDeleteLoginTemplate } from 'apollo/templates/clinic';

type AdminAuthState = { clinicId: string; isDeleteAuth: boolean } | null;

const parseAdminState = (stateParam: string | null): AdminAuthState => {
    if (!stateParam) return null;
    try {
        const decoded = JSON.parse(atob(stateParam));
        if (decoded?.clinicId) {
            return { clinicId: decoded.clinicId, isDeleteAuth: !!decoded.isDeleteAuth };
        }
    } catch {
        // not an admin state
    }
    return null;
};

const ThreeShapeAuthLandingPage: FC = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const [authState, setAuthState] = useState(AuthState.AUTH_IN_PROGRESS);
    const [isAdminClinicFlow, setIsAdminClinicFlow] = useState(false);
    const { t: translate } = useTranslation(['scan-cases', 'organization']);
    const hasRun = useRef(false);

    const handleAdminSuccess = () => {
        setIsAdminClinicFlow(true);
        setAuthState(AuthState.AUTH_SUCCESSFUL);
        setTimeout(() => navigate('/clinics'), 3000);
    };

    const [threeShapeTokenLogin] = useMutation(threeShapeTokenLoginTemplate, {
        onCompleted: ({ threeShapeTokenLogin }) => {
            if (threeShapeTokenLogin) {
                setAuthState(AuthState.AUTH_SUCCESSFUL);
                setTimeout(() => navigate('/orders/scanner-cases'), 3000);
                return;
            }
            setAuthState(AuthState.AUTH_FAILD);
        },
        onError: () => setAuthState(AuthState.AUTH_FAILD),
    });

    const [threeShapeClinicLogin] = useMutation(threeShapeClinicLoginTemplate, {
        onCompleted: ({ threeShapeClinicLogin }) => {
            if (threeShapeClinicLogin) {
                handleAdminSuccess();
            } else {
                setAuthState(AuthState.AUTH_FAILD);
            }
        },
        onError: () => setAuthState(AuthState.AUTH_FAILD),
    });

    const [threeShapeClinicDeleteLogin] = useMutation(threeShapeClinicDeleteLoginTemplate, {
        onCompleted: ({ threeShapeClinicDeleteLogin }) => {
            if (threeShapeClinicDeleteLogin) {
                handleAdminSuccess();
            } else {
                setAuthState(AuthState.AUTH_FAILD);
            }
        },
        onError: () => setAuthState(AuthState.AUTH_FAILD),
    });

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;
        const params = new URLSearchParams(search);
        const authCode = params.get('code');
        const adminState = parseAdminState(params.get('state'));

        if (!authCode) {
            setAuthState(AuthState.AUTH_FAILD);
            return;
        }

        if (adminState) {
            const mutation = adminState.isDeleteAuth ? threeShapeClinicDeleteLogin : threeShapeClinicLogin;
            mutation({ variables: { clinicId: adminState.clinicId, authCode } });
            return;
        }

        threeShapeTokenLogin({ variables: { authCode } });
    }, [search]);

    return (
        <Fragment>
            <Box display="flex" justifyContent="center" sx={{ minHeight: 300 }}>
                <Box sx={{ margin: 'auto', maxWidth: 600 }}>
                    {(() => {
                        switch (authState) {
                            case AuthState.AUTH_IN_PROGRESS:
                                return (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <CircularProgress variant="indeterminate" color="secondary" />
                                        <Typography variant="h3" mt={2}>
                                            {translate('scan-cases:PROCESSING_THREE_SHAPE_LOGIN')}
                                        </Typography>
                                    </Box>
                                );
                            case AuthState.AUTH_SUCCESSFUL:
                                return (
                                    <Alert severity="success">
                                        <AlertTitle>
                                            {translate('scan-cases:THREE_SHAPE_AUTH_SUCCESSFUL_TITLE')}
                                        </AlertTitle>
                                        <Box>
                                            {isAdminClinicFlow
                                                ? translate('organization:THREE_SHAPE_CLINIC_AUTH_SUCCESSFUL_BODY')
                                                : translate('scan-cases:SCAN_CASE_AUTH_SUCCESSFUL_BODY')}
                                        </Box>
                                    </Alert>
                                );
                            case AuthState.AUTH_FAILD:
                                return (
                                    <Alert severity="error">
                                        <AlertTitle>{translate('scan-cases:THREE_SHAPE_AUTH_ERROR_TITLE')}</AlertTitle>
                                        <Box>{translate('scan-cases:THREE_SHAPE_AUTH_ERROR_BODY')}</Box>
                                    </Alert>
                                );
                            default:
                                return null;
                        }
                    })()}
                </Box>
            </Box>
        </Fragment>
    );
};

enum AuthState {
    AUTH_IN_PROGRESS,
    AUTH_SUCCESSFUL,
    AUTH_FAILD,
}

export default ThreeShapeAuthLandingPage;
